import React, { useState } from "react";
import PropTypes from "prop-types";

// components:
import Heading from "core/Heading";
import CardLinks from "./CardLinks";
import SavedCardForm from "./SavedCardForm";
import EmptyForm from "./EmptyForm";
import OrderSummary from "./OrderSummary";
import FormButtons from "./FormButtons";

// styles:
import colors from "core/colors";
import { StyledPhotoCheckoutForm, GeneralFormError } from "./styles";

const CheckoutForm = ({
    className,
    hideCardLinks = false,
    // CardLinks props:
    hasSavedCard, // also used in other components
    saveCardCheckboxOnClick,
    saveCardInitialValue,
    // SavedCardForm/EmptyForm props:
    response,
    form,
    handleFormChange,
    generalFormError,
    // OrderSummary props:
    showOrderSummary = true,
    isSubscription,
    orderSummaryTextLine1,
    orderSummaryTextLine2,
    thumbnail,
    itemTitle,
    itemPrice,
    itemPriceOrderSummaryItem,
    // FormButtons props:
    submitText,
    isSubmitDisabled,
    submitFormWithNewCardInfo,
    submitFormWithSavedCardInfo,
    cancelLink,
    loading
}) => {
    const [showSavedCard, setShowSavedCard] = useState(hasSavedCard);

    const handleUseCardClick = e => {
        e.preventDefault();
        setShowSavedCard(!showSavedCard);
    };

    return (
        <div className={className}>
            <Heading
                type="h2"
                size="xs"
                bottomSpacing="lg"
                headingColor={colors.midnight}
            >
                Payment Information
            </Heading>

            {!hideCardLinks && (
                <CardLinks
                    showSavedCard={showSavedCard}
                    hasSavedCard={hasSavedCard}
                    handleUseCardClick={handleUseCardClick}
                    saveCardCheckboxOnClick={saveCardCheckboxOnClick}
                    saveCardInitialValue={saveCardInitialValue}
                />
            )}

            <StyledPhotoCheckoutForm>
                <form className="stripe-form">
                    {showSavedCard ? (
                        <SavedCardForm response={response} />
                    ) : (
                        <EmptyForm
                            form={form}
                            handleFormChange={handleFormChange}
                        />
                    )}
                    <OrderSummary
                        thumbnail={thumbnail}
                        itemTitle={itemTitle}
                        itemPrice={itemPrice}
                        itemPriceOrderSummaryItem={itemPriceOrderSummaryItem}
                        orderSummaryTextLine1={orderSummaryTextLine1}
                        orderSummaryTextLine2={orderSummaryTextLine2}
                        showOrderSummary={showOrderSummary}
                        isSubscription={isSubscription}
                    />
                    {!!generalFormError && (
                        <GeneralFormError>{generalFormError}</GeneralFormError>
                    )}
                    <FormButtons
                        isSubmitDisabled={isSubmitDisabled}
                        showSavedCard={showSavedCard}
                        submitFormWithNewCardInfo={submitFormWithNewCardInfo}
                        submitFormWithSavedCardInfo={
                            submitFormWithSavedCardInfo
                        }
                        cancelLink={cancelLink}
                        submitText={submitText}
                        loading={loading}
                    />
                </form>
            </StyledPhotoCheckoutForm>
        </div>
    );
};

CheckoutForm.propTypes = {
    hideCardLinks: PropTypes.bool,
    hasSavedCard: PropTypes.bool.isRequired,
    saveCardCheckboxOnClick: PropTypes.func.isRequired,
    saveCardInitialValue: PropTypes.bool.isRequired,
    response: PropTypes.object,
    form: PropTypes.object,
    handleFormChange: PropTypes.func.isRequired,
    generalFormError: PropTypes.string,
    orderSummaryTextLine1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ]),
    orderSummaryTextLine2: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ]),
    thumbnail: PropTypes.string,
    itemTitle: PropTypes.string,
    itemPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    itemPriceOrderSummaryItem: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    submitText: PropTypes.string,
    isSubmitDisabled: PropTypes.bool.isRequired,
    submitFormWithNewCardInfo: PropTypes.func.isRequired,
    submitFormWithSavedCardInfo: PropTypes.func.isRequired,
    cancelLink: PropTypes.string.isRequired,
    showOrderSummary: PropTypes.bool,
    isSubscription: PropTypes.string
};

export default CheckoutForm;
